import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUsers,
  getUserById,
  getUserActivity,
  getUserJourney,
  getProperties,
  createUser,
  updateUser,
  blockUser,
  unblockUser,
  resetUserPassword,
  deleteUser,
  type UpdateUserData,
  type BlockUserData,
  type ResetPasswordData,
} from '@/api/endpoints/users';

interface UseUsersOptions {
  status?: string;
  role?: string;
  page?: number;
  limit?: number;
  search?: string;
  onlyProducers?: boolean;
  onlyFuncionarios?: boolean;
}

export const useUsers = (options?: UseUsersOptions) => {
  return useQuery({
    queryKey: ['users', options],
    queryFn: () => getUsers(options),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => getUserById(id),
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: !!id,
  });
};

export const useUserActivity = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['user-activity', id],
    queryFn: () => getUserActivity(id),
    staleTime: 1000 * 60,
    retry: 1,
    enabled: enabled && !!id,
  });
};

export const useUserJourney = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['user-journey', id],
    queryFn: () => getUserJourney(id),
    staleTime: 1000 * 60,
    retry: 1,
    enabled: enabled && !!id,
  });
};

export const useProperties = (search?: string, enabled = true) => {
  return useQuery({
    queryKey: ['properties', search ?? ''],
    queryFn: () => getProperties(search),
    staleTime: 1000 * 60 * 2,
    retry: 1,
    enabled,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) => updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
    },
  });
};

export const useBlockUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BlockUserData }) => blockUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUnblockUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => unblockUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useResetUserPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ResetPasswordData }) =>
      resetUserPassword(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};
